import React, {createContext, Dispatch, useContext, useEffect, useMemo, useReducer, useRef} from "react";
import {
  ProductList,
  ProductListFilterItemValueType,
  IItemContent,
  IItemSearchResponse,
  ProductListFilterItemType,
  IAvailableFilter,
} from "@connox/typescript-library";
import {ProductListOrder, ProductListOrderBy} from "@connox/typescript-library";
import window from "../../lib/WindowExtended";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import {Tracking} from "../../../tracking/tracking";
import {getABTestVariant} from "../../../helpers/getABTestVariant";
import {mapUrlParamsToFilterState} from "./helpers/mapUrlParamsToFilterState";
import {buildRequestString} from "./helpers/buildRequestString";
import {reducer} from "./reducer";
import {ProductListAction} from "./actions";
import {convertToFilterTypes} from "./helpers/convertToFilterTypes";
import {getShopId} from "../../lib/getShopId";
import {useMediaQuery} from "react-responsive";

export type ProductListState = {
  instance?: ProductList;
  offset: number;
  limit: number;
  orderBy?: ProductListOrderBy;
  order?: ProductListOrder;
  loading: boolean;
  items: IItemContent[];
  filters: {[k: string]: ProductListFilterItemValueType};
  userId?: string;
  total: number;
  isLastPage: boolean;
  showFilters: boolean;
  trigger?: boolean;
  rangeFilterMap?: Map<string, {min: number; max: number}>;
};

export const initialState: ProductListState = {
  offset: 0,
  limit: ProductList.PRODUCT_LIST_DEFAULT_LIMIT,
  loading: false,
  items: [],
  filters: {},
  total: 0,
  isLastPage: true,
  showFilters: false,
  rangeFilterMap: new Map(),
};

interface IProductListContext {
  state: ProductListState;
  dispatch: Dispatch<ProductListAction>;
}

export const ProductListContext = createContext<IProductListContext>({
  state: initialState,
  dispatch: () => initialState,
});

export function useProductListContext(): IProductListContext {
  return useContext<IProductListContext>(ProductListContext);
}

const storageIdentifier = "productlist";

const ProductListProvider: React.FC<{
  children?: React.ReactNode;
  shopId: number;
  categoryId: number;
  customerId: number;
  visitorId: string;
  customerGroupId: number;
  currency?: string;
}> = ({children, shopId, categoryId, customerGroupId, customerId, currency = "USD", visitorId}) => {
  const firstState = useMemo(() => {
    const stateByUrlParams = mapUrlParamsToFilterState(initialState).state;
    // Only for DE: determines what test case is used, depending on visitor id.
    // Also to keep in mind: the last sort option in `ProductSortInput.tsx`
    const isInnofindDefaultSort = getShopId() === 100001 && getABTestVariant(visitorId) === "A";

    // Track the according sort option to exponea
    Tracking.sendABTestSeenEvent(isInnofindDefaultSort ? "innofind" : "default");

    return {
      ...stateByUrlParams,
      instance: new ProductList(
        shopId,
        categoryId,
        currency,
        undefined,
        customerId ?? undefined,
        customerGroupId ?? undefined,
      ),
      ...(isInnofindDefaultSort && !stateByUrlParams.orderBy
        ? {orderBy: "innofind" as ProductListOrderBy, userId: visitorId, order: "asc" as ProductListOrder}
        : {}),
    };
  }, []);

  const browserStorage = new BrowserStorage<IItemSearchResponse>("sessionStorage", storageIdentifier, true);
  const isMobile = useMediaQuery({query: "(max-width: 480px)"});
  const [state, dispatch] = useReducer(reducer, firstState);

  const useDidMount = (func: () => void, deps) => {
    const didMount = useRef<boolean>(false);

    useEffect(() => {
      if (didMount.current) func();
      else didMount.current = true;
    }, deps);
  };

  useDidMount(() => {
    dispatch({
      type: "RESET",
      payload: new ProductList(
        shopId,
        categoryId,
        currency,
        undefined,
        customerId ?? undefined,
        customerGroupId ?? undefined,
      ),
    });
  }, [currency, customerId, customerGroupId]);

  const cleanStorageByCategory = (params: {
    categoryId?: number;
    customerId?: number;
    customerGroupId?: number;
  }): void => {
    const relativeKeys =
      sessionStorage && Object.keys(sessionStorage).filter((key) => key.startsWith(storageIdentifier));
    if (!relativeKeys) {
      return;
    }

    const requestParams = new URLSearchParams(relativeKeys[0]?.substr(storageIdentifier.length + 1));
    if (
      Number(requestParams.get("categoryId")) !== params.categoryId ||
      Number(requestParams.get("customerId")) !== params.customerId ||
      Number(requestParams.get("customerGroupId")) !== params.customerGroupId
    ) {
      relativeKeys.forEach((key) => {
        browserStorage.remove(key);
      });
    }
  };

  useEffect(() => {
    const {instance} = state;
    if (!instance) {
      return;
    }
    const onPopState = () => {
      const {state: mappedState, filters: mappedFilters} = mapUrlParamsToFilterState(state);
      const {limit, order, orderBy} = mappedState;
      dispatch({type: "SET_SORT", payload: {orderBy, order}});
      dispatch({
        type: "SET_FILTERS",
        payload: {filters: convertToFilterTypes(mappedFilters, instance.filter.filterItems)},
      });
      dispatch({type: "TRIGGER", payload: mappedFilters});
      dispatch({type: "SET_LIMIT", payload: limit});
    };

    if (state.instance) {
      window.addEventListener("popstate", onPopState);
      cleanStorageByCategory({...state.instance.requestParams});
    }

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [state.instance]);

  const initialRangeFilters = (filters: IAvailableFilter[]) => {
    const {instance} = state;

    if (!instance) {
      return;
    }
    const rangeFiltersFields = Array.from(instance.filter.filterItems).reduce((acc: string[], [key, keyValue]) => {
      if (keyValue.type === ProductListFilterItemType.range && state.filters[key] !== undefined) {
        acc.push(key);
      }

      return acc;
    }, []);

    if (!rangeFiltersFields.length) {
      filters.forEach((filter) => {
        if (filter.type === ProductListFilterItemType.range)
          dispatch({
            type: "SET_RANGE_FILTERS_VALUES",
            payload: {
              field: filter.field,
              value: {
                min: Math.floor(Number(filter.minValue ?? 0)),
                max: Math.ceil(Number(filter.maxValue ?? 0)),
              },
            },
          });
      });
    }
  };

  const handleResponse = (data: IItemSearchResponse, isStorageUsed = false) => {
    const {instance} = state;
    if (!instance) {
      return;
    }

    initialRangeFilters(data.availableFilters);

    const toSet = instance.offset === 0 || isStorageUsed;

    // check if first load and if search param filters contains state filters
    const {state: mappedState, filters: paramsFilters} = mapUrlParamsToFilterState(state);
    if (mappedState.showFilters) {
      dispatch({
        type: "SHOW_FILTERS",
        payload: true,
      });
    }
    if (Object.keys(paramsFilters).some((filterKey) => !state.filters[filterKey])) {
      dispatch({
        type: "SET_FILTERS",
        payload: {filters: convertToFilterTypes(paramsFilters, instance.filter.filterItems)},
      });
    } else {
      dispatch({
        type: toSet ? "SET_ITEMS" : "ADD_ITEMS",
        payload: {
          items: data.itemContent,
          total: data.totalSearchHits,
        },
      });
    }
  };

  const saveDataToSessionStorage = (data: IItemSearchResponse) => {
    const keyToSave = buildRequestString(state);
    const {offset} = state;
    if (offset && sessionStorage) {
      const keysToReplace = Object.keys(sessionStorage).filter((key) => key.startsWith(storageIdentifier));

      keysToReplace.forEach((keyToRemove) => {
        const requestParams = new URLSearchParams(keyToRemove.substr(storageIdentifier.length + 1));
        const limit = Number(requestParams.get("limit"));
        if (limit > ProductList.PRODUCT_LIST_DEFAULT_LIMIT * 2) {
          browserStorage.remove(keyToRemove);
        }
      });
    }
    browserStorage.write(keyToSave, data);
  };

  const preLoading = (firstResponse: IItemSearchResponse) => {
    const instance = state.instance;
    if (!instance) {
      return;
    }
    const paramsFilters = mapUrlParamsToFilterState(state).filters;
    if (Object.keys(paramsFilters).every((key) => !!state.filters[key])) {
      instance.offset = state.offset + ProductList.PRODUCT_LIST_DEFAULT_LIMIT;
      void instance.send().then((nextResponse) => {
        const nextResponseCopy = Object.assign({}, nextResponse);
        nextResponseCopy.itemContent = [...firstResponse.itemContent, ...nextResponseCopy.itemContent];
        saveDataToSessionStorage(nextResponseCopy);
      });
    }
  };

  const getDataByLimit = (limit: number): IItemSearchResponse | null => {
    const requestString = buildRequestString(state);
    const params = new URLSearchParams(requestString);
    params.delete("limit");
    const requestStringNoLimit = params.toString();
    const maxLimitKey =
      sessionStorage &&
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(storageIdentifier))
        .filter((key) => {
          const keyParams = new URLSearchParams(key);
          keyParams.delete("limit");
          const KeyParamsNoLimit = keyParams.toString().substr(storageIdentifier.length + 1);

          return KeyParamsNoLimit === requestStringNoLimit;
        })
        .sort((key1, key2) => {
          const params1 = new URLSearchParams(key1);
          const params2 = new URLSearchParams(key2);

          return Number(params2.get("limit")) - Number(params1.get("limit"));
        })[0];

    if (!maxLimitKey) {
      return null;
    }

    const maxKeyParams = new URLSearchParams(maxLimitKey);
    const maxLimitStored = Number(maxKeyParams.get("limit"));
    let storageData: IItemSearchResponse | null = null;
    if (limit <= maxLimitStored) {
      const maxStorageData = browserStorage.read(maxLimitKey);
      if (maxStorageData) {
        storageData = maxStorageData;
        storageData.itemContent = storageData.itemContent.slice(0, limit);
      }
    }

    if (limit === maxLimitStored && storageData) {
      preLoading(storageData);
    }

    return storageData;
  };

  const prepareRequest = () => {
    if (!state.instance) {
      return;
    }

    const instance = state.instance;
    instance.offset = state.offset;
    instance.limit = state.limit;
    instance.filter.applyFilterValue(state.filters);
    instance.orderBy = state.orderBy;
    if (state.orderBy === "innofind") {
      instance.userId = visitorId;
    } else {
      instance.userId = undefined;
    }
    instance.order = state.order;

    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    const storageData = getDataByLimit(instance.limit + instance.offset);

    if (storageData) {
      // When data locally available
      instance.filter.applyFilterResult(storageData.availableFilters);
      instance.items = storageData.itemContent;
      instance.total = storageData.totalSearchHits;
      handleResponse(storageData, true);
      setTimeout(() => {
        dispatch({
          type: "SET_LOADING",
          payload: false,
        });
      }, 400);
    } else {
      // else get by api call
      instance
        .send()
        .then((response) => {
          const firstResponseCopy = Object.assign({}, response);
          if (instance.offset) {
            firstResponseCopy.itemContent = [...state.items, ...firstResponseCopy.itemContent];
          }
          handleResponse(response);
          saveDataToSessionStorage(firstResponseCopy);
          preLoading(firstResponseCopy);
        })
        .finally(() => {
          dispatch({
            type: "SET_LOADING",
            payload: false,
          });
        });
    }
  };

  // this effect will update the max and min values for range filters after every change of filters state
  // its done that way as work around because its very costly to do this in the backend (Pflaster Lösung)
  useEffect(() => {
    const instance = state.instance;
    if (!instance || isMobile) {
      return;
    }

    const availableRangeFiltersFields = Array.from(instance.filter.filterItems).reduce(
      (acc: string[], [key, keyValue]) => {
        if (keyValue.type === ProductListFilterItemType.range) {
          acc.push(key);
        }

        return acc;
      },
      [],
    );

    availableRangeFiltersFields.forEach((key) => {
      const filtersNoKey = Object.assign({}, state.filters);
      delete filtersNoKey[key];

      const instanceCopy = Object.assign(Object.create(Object.getPrototypeOf(instance)), instance) as ProductList;

      instanceCopy.filter.applyFilterValue(filtersNoKey);
      instanceCopy.limit = 1;
      instanceCopy.offset = 0;

      if (availableRangeFiltersFields.some((rangeKey) => !!filtersNoKey[rangeKey])) {
        void instanceCopy.send().then((data) => {
          data.availableFilters.forEach((filter) => {
            if (filter.type === ProductListFilterItemType.range && filter.field === key) {
              dispatch({
                type: "SET_RANGE_FILTERS_VALUES",
                payload: {
                  field: filter.field,
                  value: {
                    min: Math.floor(Number(filter.minValue ?? 0)),
                    max: Math.ceil(Number(filter.maxValue ?? 0)),
                  },
                },
              });
            }
          });
        });
      }
    });
  }, [JSON.stringify(state.filters), state.instance?.filter.filterItems.size]);

  useEffect(prepareRequest, [JSON.stringify(state.filters), state.orderBy, state.order, state.offset, state.limit]);

  return <ProductListContext.Provider value={{state, dispatch}}>{children}</ProductListContext.Provider>;
};

export default ProductListProvider;
